<template>
	<BlockVisibilityObserver title="RESULTS">
		<section class="kova-lp-results">
			<div class="kova-lp-results__inner">
				<div class="kova-lp-results__copy">
					<p class="kova-lp-results__heading">{{ heading }}</p>
					<p class="kova-lp-results__text">{{ text }}</p>
				</div>

				<div class="kova-lp-results__carousel">
					<p class="kova-lp-results__caption">{{ caption }}</p>

					<div class="kova-lp-results__stage">
						<div ref="viewport" class="kova-lp-results__viewport" @scroll.passive="onScroll">
							<div v-for="item in items" :key="item.name" class="kova-lp-results__slide">
								<span class="kova-lp-results__shot">
									<img alt="" :src="item.before" />
								</span>
								<span class="kova-lp-results__shot">
									<img alt="" :src="item.after" />
								</span>
								<p class="kova-lp-results__label">
									<span>{{ item.name }},</span>
									<span class="kova-lp-results__delta">{{ item.delta }}</span>
								</p>
							</div>
						</div>

						<template v-if="items.length > 1">
							<button
								aria-label="Previous result"
								class="kova-lp-results__arrow kova-lp-results__arrow_prev"
								type="button"
								@click="go(-1)"
							>
								<InlineSvg :src="chevronSm" />
							</button>
							<button
								aria-label="Next result"
								class="kova-lp-results__arrow kova-lp-results__arrow_next"
								type="button"
								@click="go(1)"
							>
								<InlineSvg :src="chevronSm" />
							</button>
						</template>
					</div>

					<div v-if="items.length > 1" class="kova-lp-results__dots">
						<button
							v-for="(item, index) in items"
							:key="item.name"
							:aria-label="`Result ${index + 1}`"
							class="kova-lp-results__dot"
							:class="{ kova_lp_results__dot_active: index === active }"
							type="button"
							@click="goTo(index)"
						/>
					</div>
				</div>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import chevronSm from '@/assets/kova/landing/chevron-sm.svg'
import resultBrianAfter from '@/assets/kova/landing/result-brian-after.jpg'
import resultBrianBefore from '@/assets/kova/landing/result-brian-before.jpg'
import resultChrisAfter from '@/assets/kova/landing/result-chris-after.jpg'
import resultChrisBefore from '@/assets/kova/landing/result-chris-before.jpg'
import resultJamesAfter from '@/assets/kova/landing/result-james-after.jpg'
import resultJamesBefore from '@/assets/kova/landing/result-james-before.jpg'
import resultRachelAfter from '@/assets/kova/landing/result-rachel-after.jpg'
import resultRachelBefore from '@/assets/kova/landing/result-rachel-before.jpg'
import resultSarahAfter from '@/assets/kova/landing/result-sarah-after.jpg'
import resultSarahBefore from '@/assets/kova/landing/result-sarah-before.jpg'

// The customer cards from the Reviews board (Figma 4789:72186 … 4789:72230), keyed by the
// `customers` prop. The photos are already framed, so `cover` centres them with no per-shot
// tuning.
const CUSTOMERS = {
	brian: {
		after: resultBrianAfter,
		before: resultBrianBefore,
		// Figma labels this card -60 lbs; aligned with the review copy at -42.
		delta: '-42 lbs',
		name: 'Brian',
	},
	chris: { after: resultChrisAfter, before: resultChrisBefore, delta: '-51 lbs', name: 'Chris' },
	james: { after: resultJamesAfter, before: resultJamesBefore, delta: '-48 lbs', name: 'James' },
	rachel: { after: resultRachelAfter, before: resultRachelBefore, delta: '-51 lbs', name: 'Rachel' },
	sarah: { after: resultSarahAfter, before: resultSarahBefore, delta: '-90 lbs', name: 'Sarah' },
}

export default {
	name: 'LandingKovaResults',

	components: { BlockVisibilityObserver },

	props: {
		caption: {
			type: String,
			required: true,
		},

		// ['sarah', 'james', …] — keys of CUSTOMERS, one card each, in order.
		customers: {
			type: Array,
			required: true,
		},

		heading: {
			type: String,
			required: true,
		},

		text: {
			type: String,
			required: true,
		},
	},

	setup() {
		return { chevronSm }
	},

	data() {
		return {
			active: 0,
		}
	},

	computed: {
		items() {
			return this.customers.map((customer) => CUSTOMERS[customer])
		},
	},

	methods: {
		// Scroll-snap does the paging; the arrows and dots just drive scrollLeft, so
		// swiping and clicking stay in sync without any transform bookkeeping.
		go(step) {
			this.goTo(Math.min(Math.max(this.active + step, 0), this.items.length - 1))
		},

		goTo(index) {
			const viewport = this.$refs.viewport
			viewport.scrollTo({ left: index * viewport.clientWidth, behavior: 'smooth' })
		},

		onScroll() {
			const viewport = this.$refs.viewport
			this.active = Math.round(viewport.scrollLeft / viewport.clientWidth)
		},
	},
}
</script>

<style lang="scss" scoped>
// Mobile (Figma 5026:7571): one centred column.
.kova-lp-results {
	font-family: var(--kova-lp-font);
	background: var(--kova-lp-page);
}

.kova-lp-results__inner {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding: var(--size-32) var(--size-20) var(--size-48);
}

.kova-lp-results__copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);

	text-align: center;
}

.kova-lp-results__heading {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: 1.12;
	letter-spacing: -0.64px;

	color: var(--kova-lp-text);
}

.kova-lp-results__text {
	margin: 0;

	font-size: var(--size-16);
	line-height: 1.5;

	color: var(--kova-lp-brand);
}

.kova-lp-results__carousel {
	display: flex;
	flex-direction: column;
	gap: var(--size-20);
}

.kova-lp-results__caption {
	margin: 0;

	font-size: var(--size-20);
	font-weight: 500;
	line-height: var(--size-26);
	letter-spacing: -0.4px;

	color: var(--kova-lp-text);
	text-align: center;
}

.kova-lp-results__stage {
	position: relative;
}

.kova-lp-results__viewport {
	display: flex;
	overflow-x: auto;

	scroll-snap-type: x mandatory;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
}

.kova-lp-results__slide {
	position: relative;
	box-sizing: border-box;

	display: flex;
	flex: 0 0 100%;
	gap: var(--size-8);
	padding: var(--size-8);

	border-radius: var(--size-16);
	background: var(--kova-lp-surface);
	scroll-snap-align: center;
}

.kova-lp-results__shot {
	overflow: hidden;

	flex: 1;
	min-width: 0;

	border-radius: var(--size-8);
	aspect-ratio: 163 / 295;

	img {
		width: 100%;
		height: 100%;

		object-fit: cover;
	}
}

.kova-lp-results__label {
	position: absolute;
	left: var(--size-16);
	right: var(--size-16);
	bottom: var(--size-16);

	display: flex;
	justify-content: center;
	gap: var(--size-4);
	margin: 0;
	padding: var(--size-4) var(--size-12);

	font-family: var(--font-family);
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-22);

	border-radius: var(--size-8);
	color: var(--Text-Primary-100);
	background: var(--Fill-White);
}

.kova-lp-results__delta {
	color: var(--kova-lp-rose);
}

// Arrows are a desktop affordance (4841:21267 / 4841:21269); mobile swipes.
.kova-lp-results__arrow {
	display: none;
}

.kova-lp-results__dots {
	display: flex;
	justify-content: center;
	gap: var(--size-8);
}

.kova-lp-results__dot {
	width: var(--size-8);
	height: var(--size-8);
	padding: 0;

	border: none;
	border-radius: var(--size-12);
	background: var(--kova-lp-dot);
	cursor: pointer;
}

.kova_lp_results__dot_active {
	background: var(--kova-lp-control);
}

// Desktop (Figma 5024:7321): copy on the left, carousel on the right.
@media (min-width: 1024px) {
	.kova-lp-results {
		background: var(--kova-lp-band-desktop); // shares the calculator band on desktop
	}

	// 1216px of content between Figma's 112px gutters (560 + 64 + 592).
	.kova-lp-results__inner {
		box-sizing: border-box;
		max-width: 1296px;
		margin-inline: auto;
		padding: 108px var(--size-40) 68px; // 108/68 — section paddings, off the ladder

		flex-direction: row;
		align-items: start;
		justify-content: center;
		gap: var(--size-64);
	}

	.kova-lp-results__copy {
		flex: 1 1 560px;
		min-width: 0;
		gap: var(--size-32);

		text-align: left;
	}

	.kova-lp-results__heading {
		font-size: var(--size-56);
		line-height: 1.1;
		letter-spacing: -1.12px;
	}

	.kova-lp-results__text {
		font-size: var(--size-20);
	}

	.kova-lp-results__carousel {
		flex: 1 1 592px;
		min-width: 0;
		gap: var(--size-20);
	}

	.kova-lp-results__caption {
		font-size: var(--size-32);
		line-height: 1.2;
		letter-spacing: -0.64px;
	}

	.kova-lp-results__stage {
		display: flex;
		flex-direction: column;
		gap: var(--size-16);
	}

	.kova-lp-results__slide {
		gap: var(--size-8);
		padding: var(--size-8);

		border-radius: var(--size-24);
	}

	.kova-lp-results__shot {
		border-radius: var(--size-16);
		aspect-ratio: 284 / 360;
	}

	.kova-lp-results__label {
		left: 50%;
		right: auto;
		bottom: 26px; // --size-26 is 27.44px in _sizes.scss, so the literal is exact

		border-radius: var(--size-8);
		transform: translateX(-50%);
		white-space: nowrap;
	}

	.kova-lp-results__arrow {
		position: absolute;
		top: 50%;

		display: flex;
		align-items: center;
		padding: var(--size-4);

		border: none;
		border-radius: 120px;
		color: var(--Fill-White);
		background: rgba(255, 255, 255, 0.4); // text/tertiary-white
		cursor: pointer;
		transform: translateY(-50%);
	}

	.kova-lp-results__arrow :deep(svg) {
		display: block;
		width: var(--size-24);
		height: var(--size-24);
	}

	.kova-lp-results__arrow_prev {
		left: var(--size-16);
	}

	.kova-lp-results__arrow_next {
		right: var(--size-16);
		transform: translateY(-50%) rotate(180deg);
	}

	.kova-lp-results__dot {
		width: var(--size-12);
		height: var(--size-12);
	}
}
</style>
